import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import { useT, PublicLangToggle } from "@/i18n";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_BG = { open: "bg-amber-100 text-amber-800", in_progress: "bg-blue-100 text-blue-800", deferred: "bg-slate-200 text-slate-700", complete: "bg-emerald-100 text-emerald-800" };
const PRIORITY_BG = { low: "bg-slate-100 text-slate-600", med: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700 font-black", urgent: "bg-red-600 text-white font-black animate-pulse" };

function statsForRoom(items, room) {
  const s = { total: 0, open: 0, in_progress: 0, complete: 0, deferred: 0, high_open: 0, urgent_open: 0, photo_gap: 0 };
  for (const it of items) {
    if (it.room !== room) continue;
    s.total++;
    s[it.status] = (s[it.status] || 0) + 1;
    if (it.status !== "complete" && it.priority === "high") s.high_open++;
    if (it.status !== "complete" && it.priority === "urgent") s.urgent_open++;
    const need = parseInt(it.min_photos || 0);
    const have = (it.photos || []).length;
    if (it.status !== "complete" && need > have) s.photo_gap += (need - have);
  }
  return s;
}

function Gauge({ pct, done, occupied, size = 40 }) {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  const color = occupied ? "#dc2626" : done ? "#10b981" : pct >= 66 ? "#3b82f6" : pct > 0 ? "#f59e0b" : "#cbd5e1";
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}/>
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize="9" fontWeight="900" fill={color}>
        {occupied ? "🚫" : done ? "✓" : `${pct}%`}
      </text>
    </svg>
  );
}

function ReadOnlyTile({ label, stats, onClick, occupied }) {
  const { t } = useT();
  const pct = stats.total === 0 ? 0 : Math.round((stats.complete / stats.total) * 100);
  const done = stats.complete === stats.total && stats.total > 0;
  return (
    <button onClick={onClick}
      className={`text-left p-3 bg-white border-2 ${occupied ? "border-red-600 bg-red-50" : "border-slate-300"} hover:border-cyan-600 relative min-h-[110px]`}>
      {occupied && <div className="absolute top-1 right-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">{t("pub.occupied")}</div>}
      <div className="flex items-start gap-2">
        <Gauge pct={pct} done={done} occupied={occupied}/>
        <div className="min-w-0 flex-1">
          <div className="font-display font-black text-lg leading-tight uppercase tracking-tight truncate">{label}</div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mt-0.5">
            {stats.complete}/{stats.total} {t("pub.done")}
          </div>
          {stats.photo_gap > 0 && !occupied && (
            <div className="mt-1 inline-block text-[10px] font-black uppercase tracking-widest text-amber-700">📸 {stats.photo_gap} {t("pub.needed")}</div>
          )}
        </div>
      </div>
    </button>
  );
}

function ReadOnlyItem({ item }) {
  const { t } = useT();
  const statusLabel = {
    open: t("pub.status.open"), in_progress: t("pub.status.in_progress"),
    deferred: t("pub.status.deferred"), complete: t("pub.status.complete"),
  }[item.status] || item.status;
  return (
    <div className="p-3 bg-white border-2 border-slate-300 border-l-8" style={{ borderLeftColor: "#475569" }} data-testid={`ro-punch-${item.id}`}>
      <div className="flex items-center gap-1 flex-wrap mb-1">
        <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${STATUS_BG[item.status]}`}>{statusLabel}</span>
        <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${PRIORITY_BG[item.priority]}`}>{item.priority}</span>
        {item.room && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700">{item.room}</span>}
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{item.category}</span>
        {item.discovery === "post" && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-pink-500 text-white">{t("pub.new_find")}</span>}
        {item.needs_approval && item.approval_status === "pending" && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-orange-500 text-white animate-pulse">{t("pub.pending")}</span>}
        {item.approval_status === "approved" && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white">{t("pub.approved")}</span>}
        {item.approval_status === "rejected" && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">{t("pub.rejected")}</span>}
        {item.verified_by_owner && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-teal-600 text-white">{t("pub.pm_verified")}</span>}
        {item.project_only && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-violet-600 text-white">{t("pub.project_only")}</span>}
      </div>
      <div className="font-black text-sm">{item.title}</div>
      {item.description && <div className="text-xs text-slate-600 italic mt-0.5">{item.description}</div>}
      {parseInt(item.min_photos || 0) > 0 && (
        <div className={`mt-1 inline-block px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest ${(item.photos || []).length >= item.min_photos ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
          📸 {(item.photos || []).length}/{item.min_photos}
        </div>
      )}
    </div>
  );
}

export default function PublicProjectView() {
  const { t } = useT();
  const { token, unit } = useParams();
  const [data, setData] = useState(null);
  const [activeRoom, setActiveRoom] = useState(unit || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BASE}/api/public/projects/${token}`)
      .then(r => setData(r.data))
      .catch(() => toast.error(t("pub.expired")))
      .finally(() => setLoading(false));
  }, [token, t]);

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-400 text-sm">{t("common.loading")}</div>;
  if (!data) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-500">{t("pub.expired_body")}</div>;

  const allItems = data.punch_items || [];
  const rooms = data.rooms || [];
  const occ = data.occupancy || {};
  const unitRooms = rooms.filter(r => /^\d{1,2}[a-c]$/i.test(r))
    .sort((a, b) => (parseInt(a) - parseInt(b)) || a.slice(-1).localeCompare(b.slice(-1)));
  const commonRooms = rooms.filter(r => r.toLowerCase().startsWith("bldg ") && r.toLowerCase().includes("common"));
  const effectiveRoom = activeRoom ?? (unitRooms.length === 0 && rooms.length > 0 ? "__all__" : null);
  const items = effectiveRoom === "__all__"
    ? allItems
    : (effectiveRoom ? allItems.filter(i => i.room === effectiveRoom) : []);

  const kpiLabel = {
    total: t("pub.kpi.total"), open: t("pub.kpi.open"),
    in_progress: t("pub.kpi.in_progress"), complete: t("pub.kpi.complete"),
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="public-project-view">
      <Toaster richColors position="top-center"/>
      <header className="bg-[#0f172a] text-white">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-400">{t("pub.readonly")}</div>
              <div className="font-display font-black text-xl leading-tight">{data.name}</div>
              <div className="text-xs text-slate-300 mt-0.5">{data.property_address}</div>
            </div>
            <PublicLangToggle/>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {["total","open","in_progress","complete"].map(k => (
            <div key={k} className="p-3 bg-white border-2 border-[#0f172a]">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{kpiLabel[k]}</div>
              <div className="font-display font-black text-2xl">{data.kpis?.[k] || 0}</div>
            </div>
          ))}
        </div>

        {effectiveRoom ? (
          <>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <button onClick={() => setActiveRoom(null)} data-testid="ro-back-btn"
                className="h-9 px-3 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                <ArrowLeft weight="bold" size={12}/> {unitRooms.length > 0 ? t("pub.back_units") : t("pub.back_rooms")}
              </button>
              <div className="font-display font-black text-xl uppercase tracking-tight">
                {effectiveRoom === "__all__" ? t("pub.all_items", { n: items.length }) : `📍 ${effectiveRoom}`}
              </div>
              {effectiveRoom !== "__all__" && occ[effectiveRoom]?.occupied && (
                <span className="px-2 py-1 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest">{t("pub.do_not_enter")}</span>
              )}
            </div>
            {effectiveRoom === "__all__" ? (
              (() => {
                const groups = {};
                items.forEach(it => { const r = it.room || "—"; (groups[r] ||= []).push(it); });
                const orderedRooms = rooms.filter(r => groups[r]?.length).concat(Object.keys(groups).filter(r => !rooms.includes(r)));
                return (
                  <div className="space-y-4">
                    {orderedRooms.map(r => (
                      <section key={r} data-testid={`ro-group-${r.replace(/\s+/g,'-')}`}>
                        <div className="sticky top-0 z-10 bg-slate-100 py-2 flex items-baseline gap-2 border-b-2 border-[#0f172a]">
                          <div className="font-display font-black text-lg uppercase tracking-tight">📍 {r}</div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{groups[r].length}</div>
                        </div>
                        <div className="space-y-2 mt-2">
                          {groups[r].map(it => <ReadOnlyItem key={it.id} item={it}/>)}
                        </div>
                      </section>
                    ))}
                  </div>
                );
              })()
            ) : (
              <div className="space-y-2">
                {items.length === 0 && <div className="p-6 bg-white border-2 border-dashed border-slate-300 text-center text-xs text-slate-500">{t("pub.no_items", { room: effectiveRoom })}</div>}
                {items.map(it => <ReadOnlyItem key={it.id} item={it}/>)}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">{t("pub.tap_tile")}</div>
            {rooms.includes("Exterior") && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                <ReadOnlyTile label={t("pub.exterior")} stats={statsForRoom(allItems, "Exterior")} onClick={() => setActiveRoom("Exterior")}/>
                {rooms.includes("All Units") && <ReadOnlyTile label={t("pub.all_units")} stats={statsForRoom(allItems, "All Units")} onClick={() => setActiveRoom("All Units")}/>}
              </div>
            )}
            {unitRooms.length > 0 && (
              <>
                <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600 mb-2">{t("pub.units", { n: unitRooms.length })}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4" data-testid="ro-picker-units">
                  {unitRooms.map(r => (
                    <ReadOnlyTile key={r} label={r} stats={statsForRoom(allItems, r)}
                      occupied={!!occ[r]?.occupied} onClick={() => setActiveRoom(r)}/>
                  ))}
                </div>
              </>
            )}
            {commonRooms.length > 0 && (
              <>
                <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700 mb-2">{t("pub.common_areas", { n: commonRooms.length })}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-4">
                  {commonRooms.map(r => (
                    <ReadOnlyTile key={r} label={r.replace(" Common","")} stats={statsForRoom(allItems, r)} onClick={() => setActiveRoom(r)}/>
                  ))}
                </div>
              </>
            )}
          </>
        )}
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 text-center mt-6">{t("pub.footer")}</div>
      </div>
    </div>
  );
}
